import { components } from 'api/goodtrust/swagger'
import { deleteUserDevice, putUserDeviceContact } from 'api/goodtrust/user/device'
import { Button } from 'components/button/Button'
import { ContactShareItem } from 'components/contact/contactShareForm/ContactShareForm'
import {
  ContactShareInvite,
  parseSharedInvitedContacts,
} from 'components/contact/contactShareInvite/ContactShareInvite'
import { Icon } from 'components/icon'
import { ConfirmModal } from 'components/modal/confirmModal/ConfirmModal'
import { useModal } from 'components/modal/Modal'
import { usePopperModal } from 'components/modal/PopperModal'
import { handleAndToastError, toastSuccess } from 'components/Toast'
import {
  useCardStatusComplete,
  useCardStatusMissingTC,
  WizardGridCard,
} from 'components/wizard/wizardGridCard/WizardGridCard'
import { useTranslation } from 'next-i18next'
import Router from 'next/router'
import { colors } from 'utils/colors'
import { formatUpdatedAgo } from 'utils/format'
import { useLoadingCallback } from 'utils/hooks'
import { getDigitalVaultURLs } from 'utils/url'

export function DeviceCard(props: {
  device: components['schemas']['UserDeviceResponse']
  onDataChange?: () => void
  sharedWithMe?: boolean
  showType?: boolean
}) {
  const { t } = useTranslation('trusted_contact')
  const [handleShare, isSharing] = useLoadingCallback(
    async (contactShareData: ContactShareItem[]) => {
      try {
        if (props.device?.uuid == null) throw Error()
        const res = await putUserDeviceContact(
          {
            share:
              contactShareData?.map((contact) => ({
                trusted_contact_uuid: contact.uuid,
                access_type: 'READ_ONLY',
                share_mode: contact.share_mode || 'NOW',
              })) ?? [],
          },
          { uuid: props.device.uuid }
        )
        if (!res.ok) throw Error()
        toastSuccess('device_shared')
        closeMenu()
        await props.onDataChange?.()
      } catch (err) {
        handleAndToastError(err, 'failed_to_share_device')
      }
    }
  )

  const [deleteModal, showDeleteModal] = useModal(({ close, isOpen }) =>
    isOpen ? (
      <ConfirmModal
        title={t('trusted_contact.device.delete_modal_confirm_title')}
        danger
        onConfirm={async () => {
          try {
            if (props.device?.uuid == null) throw Error()
            const res = await deleteUserDevice(undefined, { uuid: props.device.uuid })
            if (!res.ok) throw Error(res?.error?.toString?.())
            toastSuccess('device_deleted')
            close()
            props.onDataChange?.()
          } catch (err) {
            handleAndToastError(err, 'failed_to_delete_device')
          }
        }}
        close={close}
      />
    ) : null
  )

  const [inviteModal, showInviteModal] = useModal(
    ({ close, isOpen }) =>
      isOpen ? (
        <ContactShareInvite
          hideAccessType
          defaultValue={parseSharedInvitedContacts(props.device.trusted_contacts)}
          onSubmit={(data) => {
            handleShare(data)
            close()
          }}
        />
      ) : null,
    {
      disableFocusLock: true,
      modalContentCss: { overflowY: 'visible' },
      mobilePlacement: 'top',
    }
  )

  const {
    modal: menu,
    setReferenceElement,
    open: openMenu,
    close: closeMenu,
  } = usePopperModal(
    () => (
      <>
        <Button
          type="button"
          variant="text"
          onClick={() => void showInviteModal()}
          leftIcon={<Icon name="Share" />}
        >
          {t('trusted_contact.device.popper_modal_share_button')}
        </Button>
        <Button
          type="button"
          variant="text"
          onClick={() => void Router.push(urls.edit)}
          leftIcon={<Icon name="DocumentEdit" />}
        >
          {t('trusted_contact.device.popper_modal_edit_button')}
        </Button>
        <Button
          type="button"
          variant="text"
          tone="danger"
          onClick={showDeleteModal}
          leftIcon={<Icon name="Bin" />}
        >
          {t('trusted_contact.device.popper_modal_delete_button')}
        </Button>
      </>
    ),
    { placement: 'bottom-end' }
  )

  const urls = getDigitalVaultURLs('device', props.device.uuid)

  const cardStatusMissingTC = useCardStatusMissingTC()
  const cardStatusComplete = useCardStatusComplete()
  return (
    <>
      {deleteModal}
      {inviteModal}
      {menu}
      <WizardGridCard
        ref={setReferenceElement}
        data-testid={`device-item-${props.device.uuid}`}
        image={<Icon name="PhoneLock" color={colors.gray[500]} />}
        entityType={props.showType ? 'Device' : null}
        css={{ minHeight: '10rem' }}
        title={props.device?.name}
        brief={props.device.modified ? formatUpdatedAgo(props.device.modified) : ''}
        isLoading={isSharing}
        openMenu={props.sharedWithMe ? undefined : openMenu}
        locked={props.device?.locked}
        onClick={() => void Router.push(props.sharedWithMe ? urls.shared : urls.edit)}
        status={!props.device.trusted_contacts?.length ? cardStatusMissingTC : cardStatusComplete}
      />
    </>
  )
}
